import React, { useState, useEffect, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import { X, Network, Copy, Check, ChevronRight, ChevronLeft, Plus, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import { Button } from '../../../../components/ui/Button';
import NestedFormRow from './components/NestedFormRow';
import {
  getDefaultWebhookAttrs,
  safeParseJSON,
  buildJsonObject,
  jsonToNodes,
} from '../../../../utils/functions';

const EditIntegrationModal = ({
  open,
  onClose,
  onConfirm,
  loading,
  initialData,
  sellers = [],
}) => {
  const [currentStep, setCurrentStep] = useState(1);

  // General Data
  const [integrationName, setIntegrationName] = useState("");
  const [integrationType, setIntegrationType] = useState("WMS");
  const [url, setUrl] = useState("");
  const [sellerIds, setSellerIds] = useState([]);

  // Inbound & Config
  const [inboundUrl, setInboundUrl] = useState(`${process.env.REACT_APP_URL || 'http://localhost:8000/'}exposed_api/webhook/inbound/update_status/`);
  const [token, setToken] = useState("");
  const [configuration, setConfiguration] = useState({
    enable_extra_actions: false,
    enable_inbound: false,
  });
  const [statusMapping, setStatusMapping] = useState([]);

  // Headers & Body
  const [headers, setHeaders] = useState([]);
  const [bodyNodes, setBodyNodes] = useState([]);

  // UI state
  const [copiedStates, setCopiedStates] = useState({});

  useEffect(() => {
    if (open && initialData) {
      setCurrentStep(1);
      setIntegrationName(initialData.integration_name || "");
      setIntegrationType(initialData.integration_type || "WMS");
      setUrl(initialData.url || "");

      // Handle sellers or seller_ids depending on backend structure
      if (initialData.sellers && Array.isArray(initialData.sellers)) {
        setSellerIds(initialData.sellers.map(s => (typeof s === 'object' ? s.id : s)));
      } else {
        setSellerIds(initialData.seller_ids || []);
      }

      setToken(initialData.token || "");
      if (initialData.inbound_url) {
        setInboundUrl(initialData.inbound_url);
      }

      const config = safeParseJSON(initialData.configuration);
      const backendHeaders = safeParseJSON(initialData.headers);
      const backendBody = safeParseJSON(initialData.body);

      setConfiguration({
        enable_extra_actions: config.enable_extra_actions === true,
        enable_inbound: config.enable_inbound === true,
      });

      if (config.status_mapping) {
        const smArray = Object.entries(config.status_mapping).map(([int, ext]) => ({
          id: Date.now().toString() + Math.random(),
          external: ext,
          internal: int,
        }));
        setStatusMapping(smArray);
      } else {
        setStatusMapping([]);
      }

      if (backendHeaders && Object.keys(backendHeaders).length > 0) {
        const headersArray = Object.entries(backendHeaders).map(([k, v]) => ({
          id: Date.now().toString() + Math.random(),
          key: k,
          value: v,
        }));
        setHeaders(headersArray);
      } else {
        setHeaders([]);
      }

      if (backendBody && Object.keys(backendBody).length > 0) {
        setBodyNodes(jsonToNodes(backendBody));
      } else {
        setBodyNodes([]);
      }
    }
  }, [open, initialData]);

  const handleCopyTag = (tag, id = tag) => {
    navigator.clipboard.writeText(tag);
    setCopiedStates(prev => ({ ...prev, [id]: true }));
    setTimeout(() => {
      setCopiedStates(prev => ({ ...prev, [id]: false }));
    }, 2000);
  };

  const toggleSeller = (id) => {
    setSellerIds(prev =>
      prev.includes(id) ? prev.filter(sId => sId !== id) : [...prev, id]
    );
  };

  // --- Handlers for Status Mappings ---
  const addStatusMappingRow = () => {
    setStatusMapping([...statusMapping, { id: Date.now().toString(), external: "", internal: "fulfilled" }]);
  };
  const updateStatusMapping = (id, field, value) => {
    setStatusMapping(statusMapping.map((sm) => sm.id === id ? { ...sm, [field]: value } : sm));
  };
  const deleteStatusMapping = (id) => {
    setStatusMapping(statusMapping.filter((sm) => sm.id !== id));
  };

  // --- Handlers for Headers ---
  const addHeaderRow = () => {
    setHeaders([...headers, { id: Date.now().toString(), key: "", value: "" }]);
  };
  const updateHeader = (id, field, value) => {
    setHeaders(headers.map((h) => h.id === id ? { ...h, [field]: value } : h));
  };
  const deleteHeader = (id) => {
    setHeaders(headers.filter((h) => h.id !== id));
  };

  // --- Handlers for Body Nodes ---
  const createNode = () => ({
    id: Date.now().toString() + Math.random(),
    key: "",
    type: "string",
    value: "",
    children: [],
  });
  const addBodyRow = () => setBodyNodes([...bodyNodes, createNode()]);
  const updateBodyNode = useCallback((nodes, id, field, value) => {
    return nodes.map((node) => {
      if (node.id === id) {
        return { ...node, [field]: value, ...(field === "type" && (value === "object" || value === "array") ? { value: "" } : {}) };
      }
      if (node.children) return { ...node, children: updateBodyNode(node.children, id, field, value) };
      return node;
    });
  }, []);
  const handleUpdateBody = useCallback((id, field, value) => {
    setBodyNodes((prev) => updateBodyNode(prev, id, field, value));
  }, [updateBodyNode]);
  const deleteBodyNode = useCallback((nodes, id) => {
    return nodes.filter((node) => node.id !== id).map((node) => {
      if (node.children) return { ...node, children: deleteBodyNode(node.children, id) };
      return node;
    });
  }, []);
  const handleDeleteBody = useCallback((id) => {
    setBodyNodes((prev) => deleteBodyNode(prev, id));
  }, [deleteBodyNode]);
  const addChildToNode = useCallback((nodes, parentId) => {
    return nodes.map((node) => {
      if (node.id === parentId) return { ...node, children: [...(node.children || []), createNode()] };
      if (node.children) return { ...node, children: addChildToNode(node.children, parentId) };
      return node;
    });
  }, []);
  const handleAddChild = useCallback((parentId) => {
    setBodyNodes((prev) => addChildToNode(prev, parentId));
  }, [addChildToNode]);

  // Submit
  const handleConfirm = () => {
    const headersObj = {};
    headers.forEach((h) => { if (h.key) headersObj[h.key] = h.value; });
    const statusMappingObj = {};
    statusMapping.forEach((sm) => { if (sm.external && sm.internal) statusMappingObj[sm.internal] = sm.external; });
    const bodyObj = buildJsonObject(bodyNodes);

    const payload = {
      integration_name: integrationName,
      integration_type: integrationType,
      url,
      seller_ids: sellerIds,
      inbound_url: inboundUrl,
      token,
      configuration: {
        enable_extra_actions: configuration.enable_extra_actions,
        enable_inbound: configuration.enable_inbound,
        status_mapping: statusMappingObj,
      },
      headers: headersObj,
      body: bodyObj,
    };
    onConfirm(payload);
  };

  if (!open) return null;

  const totalSteps = 4;
  const isStep1Valid = integrationName && url;

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div
        role="dialog"
        aria-modal="true"
        data-testid="edit-integration-modal"
        className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden border border-border">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-ninja-gray/30 dark:bg-black/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center text-cyan-600 dark:text-cyan-400">
              <Network size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-ninja-dark dark:text-white">Editar Integración Externa</h2>
              <p className="text-sm text-text-secondary">Paso {currentStep} de {totalSteps}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            aria-label="Cerrar"
            className="p-2 text-text-muted hover:text-ninja-dark dark:hover:text-white hover:bg-ninja-gray dark:hover:bg-[#2d2d2d] rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X size={20} />
          </button>
        </div>

        {/* Stepper Progress */}
        <div className="w-full bg-border h-1.5">
          <div
            className="h-full bg-ninja-green transition-all duration-300"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* STEP 1: General */}
          <div className={clsx("space-y-6", currentStep !== 1 && "hidden")}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-ninja-dark dark:text-white mb-1.5">Proveedor</label>
                <select
                  className="w-full px-4 py-2.5 bg-ninja-gray/50 dark:bg-[#111] border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all dark:text-white opacity-60 cursor-not-allowed"
                  value={integrationName}
                  disabled
                >
                  <option value="GENERIC">Genérico (Webhook)</option>
                  <option value="ALTANET">Altanet</option>
                  <option value={integrationName}>{integrationName}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-ninja-dark dark:text-white mb-1.5">Tipo de Sistema</label>
                <select
                  className="w-full px-4 py-2.5 bg-ninja-gray/50 dark:bg-[#111] border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all dark:text-white opacity-60 cursor-not-allowed"
                  value={integrationType}
                  disabled
                >
                  <option value="WMS">WMS (Bodega)</option>
                  <option value="ERP">ERP (Planificación)</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-ninja-dark dark:text-white mb-1.5">Sellers Asignados</label>
              <div className="p-3 bg-ninja-gray/30 dark:bg-[#111] border border-border rounded-xl max-h-48 overflow-y-auto flex flex-col gap-2">
                {sellers.length === 0 && (
                  <p className="text-sm text-text-secondary italic">No hay sellers disponibles.</p>
                )}
                {sellers.map(seller => (
                  <label key={seller.id} className="flex items-center gap-3 p-2 hover:bg-white dark:hover:bg-[#2d2d2d] rounded-lg cursor-pointer transition-colors border border-transparent hover:border-border">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-cyan-500 rounded border-border focus:ring-cyan-500"
                      checked={sellerIds.includes(seller.id)}
                      onChange={() => toggleSeller(seller.id)}
                    />
                    <span className="text-sm font-medium text-ninja-dark dark:text-white">{seller.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-ninja-dark dark:text-white mb-1.5">URL de Destino</label>
              <input
                type="url"
                className="w-full px-4 py-2.5 bg-ninja-gray/50 dark:bg-[#111] border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all dark:text-white"
                placeholder="https://..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
            </div>
          </div>

          {/* STEP 2: Inbound & Mappings */}
          <div className={clsx("space-y-6", currentStep !== 2 && "hidden")}>
            <div className="space-y-4">
              <label className="flex items-center gap-3 p-4 border border-border rounded-xl cursor-pointer hover:bg-ninja-gray/30 dark:hover:bg-[#2d2d2d]/30 transition-colors">
                <input
                  type="checkbox"
                  className="w-5 h-5 text-cyan-500 rounded border-border focus:ring-cyan-500"
                  checked={configuration.enable_extra_actions}
                  onChange={(e) => setConfiguration({ ...configuration, enable_extra_actions: e.target.checked })}
                />
                <div>
                  <div className="font-semibold text-ninja-dark dark:text-white">Habilitar acciones extra</div>
                  <div className="text-sm text-text-secondary">Permite triggers automáticos basados en eventos.</div>
                </div>
              </label>

              <label className="flex items-center gap-3 p-4 border border-border rounded-xl cursor-pointer hover:bg-ninja-gray/30 dark:hover:bg-[#2d2d2d]/30 transition-colors">
                <input
                  type="checkbox"
                  className="w-5 h-5 text-cyan-500 rounded border-border focus:ring-cyan-500"
                  checked={configuration.enable_inbound}
                  onChange={(e) => setConfiguration({ ...configuration, enable_inbound: e.target.checked })}
                />
                <div>
                  <div className="font-semibold text-ninja-dark dark:text-white">Habilitar recepción (Inbound)</div>
                  <div className="text-sm text-text-secondary">Permite recibir actualizaciones de estado desde el proveedor.</div>
                </div>
              </label>
            </div>

            {configuration.enable_inbound && (
              <div className="space-y-6 animate-in slide-in-from-top-2">
                <div className="p-5 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-xl space-y-4">
                  <div>
                    <h3 className="font-bold text-blue-900 dark:text-blue-400">URL de Recepción Segura</h3>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">Entregue esta URL al proveedor para que notifique los cambios de estado.</p>
                  </div>

                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <div className="w-24 text-xs font-semibold text-blue-800 dark:text-blue-300 uppercase tracking-wider self-center">URL</div>
                      <div className="flex-1 flex gap-2">
                        <input type="text" readOnly value={inboundUrl} className="flex-1 px-3 py-2 bg-white dark:bg-black/40 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-ninja-dark dark:text-white" />
                        <button onClick={() => handleCopyTag(inboundUrl, 'url')} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center">
                          {copiedStates['url'] ? <Check size={16} /> : <Copy size={16} />}
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <div className="w-24 text-xs font-semibold text-blue-800 dark:text-blue-300 uppercase tracking-wider self-center">Token</div>
                      <div className="flex-1 flex gap-2">
                        <input type="text" readOnly value={token} className="flex-1 px-3 py-2 bg-white dark:bg-black/40 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-ninja-dark dark:text-white font-mono" />
                        <button onClick={() => handleCopyTag(token, 'token')} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center">
                          {copiedStates['token'] ? <Check size={16} /> : <Copy size={16} />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-5 border border-border rounded-xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-ninja-dark dark:text-white">Mapeo de Estados</h3>
                    <button onClick={addStatusMappingRow} className="flex items-center gap-1 text-sm font-semibold text-cyan-600 dark:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 px-3 py-1.5 rounded-lg transition-colors">
                      <Plus size={16} /> Agregar Estado
                    </button>
                  </div>

                  {statusMapping.length === 0 ? (
                    <p className="text-sm text-text-secondary italic text-center py-4">No hay estados mapeados.</p>
                  ) : (
                    <div className="space-y-2">
                      {statusMapping.map((sm) => (
                        <div key={sm.id} className="flex items-center gap-2">
                          <select
                            className="flex-1 px-3 py-2 bg-ninja-gray/50 dark:bg-[#111] border border-border rounded-xl text-sm focus:outline-none focus:border-cyan-500 dark:text-white"
                            value={sm.internal}
                            onChange={(e) => updateStatusMapping(sm.id, "internal", e.target.value)}
                          >
                            <option value="fulfilled">Preparado</option>
                            <option value="shipped">Despachado</option>
                            <option value="delivered_to_courier">Entregado a courier</option>
                            <option value="in_cd">En CD</option>
                            {/* Ocultos del selector, se mantienen para mapeos ya guardados */}
                            <option value="delivered" hidden>Entregado</option>
                            <option value="failed" hidden>Fallido</option>
                          </select>
                          <span className="text-text-muted">→</span>
                          <input
                            type="text"
                            className="flex-1 px-3 py-2 bg-ninja-gray/50 dark:bg-[#111] border border-border rounded-xl text-sm focus:outline-none focus:border-cyan-500 dark:text-white"
                            placeholder="Estado Externo (Ej: paket)"
                            value={sm.external}
                            onChange={(e) => updateStatusMapping(sm.id, "external", e.target.value)}
                          />
                          <button onClick={() => deleteStatusMapping(sm.id)} className="p-2 text-text-muted hover:text-danger rounded-lg transition-colors">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* STEP 3: Headers */}
          <div className={clsx("space-y-6", currentStep !== 3 && "hidden")}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="font-bold text-ninja-dark dark:text-white">Cabeceras de la Solicitud (Headers)</h3>
                <p className="text-sm text-text-secondary">Defina los headers HTTP personalizados para el webhook.</p>
              </div>
              <button onClick={addHeaderRow} className="flex items-center gap-1 text-sm font-semibold text-cyan-600 dark:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 px-3 py-1.5 rounded-lg transition-colors">
                <Plus size={16} /> Agregar Cabecera
              </button>
            </div>

            <div className="space-y-2 p-1">
              {headers.length === 0 ? (
                <div className="text-center p-8 border-2 border-dashed border-border rounded-xl">
                  <p className="text-sm text-text-secondary">No hay cabeceras configuradas.</p>
                </div>
              ) : (
                headers.map((h) => (
                  <div key={h.id} className="flex items-center gap-3">
                    <input
                      type="text"
                      className="flex-[0.4] px-4 py-2 bg-ninja-gray/50 dark:bg-[#111] border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:text-white font-mono"
                      placeholder="Ej: Authorization"
                      value={h.key}
                      onChange={(e) => updateHeader(h.id, "key", e.target.value)}
                    />
                    <span className="text-text-muted font-mono">:</span>
                    <input
                      type="text"
                      className="flex-1 px-4 py-2 bg-ninja-gray/50 dark:bg-[#111] border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:text-white font-mono"
                      placeholder="Ej: Bearer token..."
                      value={h.value}
                      onChange={(e) => updateHeader(h.id, "value", e.target.value)}
                    />
                    <button onClick={() => deleteHeader(h.id)} className="p-2 text-text-muted hover:text-danger rounded-lg transition-colors">
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* STEP 4: Body Payload */}
          <div className={clsx("space-y-6", currentStep !== 4 && "hidden")}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="font-bold text-ninja-dark dark:text-white">Cuerpo de la Solicitud (JSON Payload)</h3>
                <p className="text-sm text-text-secondary">Construya la estructura de datos que se enviará.</p>
              </div>
              <button onClick={addBodyRow} className="flex items-center gap-1 text-sm font-semibold text-cyan-600 dark:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 px-3 py-1.5 rounded-lg transition-colors">
                <Plus size={16} /> Agregar Raíz
              </button>
            </div>

            <div className="bg-ninja-gray/30 dark:bg-[#111] border border-border rounded-xl p-4 min-h-[200px]">
              {bodyNodes.length === 0 ? (
                <div className="h-full flex items-center justify-center flex-col gap-2 text-text-secondary pt-10 pb-10">
                  <Network size={32} className="opacity-50" />
                  <p className="text-sm">Agregue un nodo raíz para comenzar a construir el JSON.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {bodyNodes.map((node) => (
                    <NestedFormRow
                      key={node.id}
                      node={node}
                      depth={0}
                      onChange={handleUpdateBody}
                      onDelete={handleDeleteBody}
                      onAddChild={handleAddChild}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Available Attributes */}
            <div className="pt-4">
              <h4 className="text-sm font-semibold text-ninja-dark dark:text-white mb-2">Variables del sistema (Clic para copiar)</h4>
              <div className="flex flex-wrap gap-2">
                {getDefaultWebhookAttrs().map((tag) => (
                  <button
                    key={tag}
                    onClick={() => handleCopyTag(tag)}
                    className="px-3 py-1.5 border border-cyan-200 dark:border-cyan-800/50 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-400 rounded-lg text-xs font-mono font-bold hover:bg-cyan-100 dark:hover:bg-cyan-900/40 transition-colors flex items-center gap-1.5"
                  >
                    {copiedStates[tag] ? <Check size={12} /> : null}
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>

        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-border bg-ninja-gray/20 dark:bg-black/20 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-text-secondary hover:text-ninja-dark dark:hover:text-white font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            Cancelar
          </button>

          <div className="flex items-center gap-3">
            {currentStep > 1 && (
              <button
                onClick={() => setCurrentStep(prev => prev - 1)}
                className="px-5 py-2.5 border border-border hover:bg-ninja-gray dark:hover:bg-[#2d2d2d] rounded-xl font-semibold transition-colors text-ninja-dark dark:text-white flex items-center gap-1"
              >
                <ChevronLeft size={18} /> Atrás
              </button>
            )}

            {currentStep < totalSteps ? (
              <button
                onClick={() => setCurrentStep(prev => prev + 1)}
                disabled={currentStep === 1 && !isStep1Valid}
                className="px-5 py-2.5 bg-ninja-green hover:bg-ninja-green/90 text-white rounded-xl font-semibold transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Siguiente <ChevronRight size={18} />
              </button>
            ) : (
              <Button
                variant="primary"
                icon={Check}
                onClick={handleConfirm}
                disabled={!isStep1Valid}
                loading={loading}
                loadingLabel="Guardando..."
              >
                Guardar Cambios
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default memo(EditIntegrationModal);
